import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import { getAllSpecs, createSpec, deleteSpec } from "../../api/specs";
import { deleteSpecFile } from "../../api/fileSystem";
import { Spec } from "../../models/specs";

import ManageSpecsCard from "../../components/SuperDashboard/ManageSpecs/SpecsCard";
import CreateSpecModal from "../../components/SuperDashboard/ManageSpecs/CreateSpecModal";
import DeleteSpecModal from "../../components/SuperDashboard/ManageSpecs/DeleteSpecModal";
import { uploadSpec } from "../../api/fileSystem";
const toProduct = (value: string): "Leads" | "Calls" | "Traffic" => {
    if (value === "Leads" || value === "Calls" || value === "Traffic") {
        return value;
    }
    return "Leads";
};

const toType = (
    value?: string | null
): "Direct Post" | "Ping Post" | "RTB" | "Availability Ping" | "N/A" => {
    if (
        value === "Direct Post" ||
        value === "Ping Post" ||
        value === "RTB" ||
        value === "Availability Ping"
    ) {
        return value;
    }
    return "N/A";
};

export default function SuperManageSpecsPage() {
    const [specs, setSpecs] = useState<Spec[]>([]);
    const [creatingSpec, setCreatingSpec] = useState(false);
    const [deletingSpec, setDeletingSpec] = useState<Spec | null>(null);

    const fetchSpecs = async () => {
        try {
            const result = await getAllSpecs({ page_size: 0 });
            setSpecs(result);
        } catch (error: any) {
            toast.error(error?.message || "Failed to fetch Specs");
        }
    };

    useEffect(() => {
        fetchSpecs();
    }, []);

    const handleCreate = async (data: {
        name: string;
        product: string[];
        type?: string;
        vertical: string;
        description?: string;
        visible: boolean;
        file: File | null;
    }) => {
        try {
            if (!data.file) {
                toast.error("HTML file required");
                return;
            }

            await uploadSpec(data.file, data.name);

            const newSpec = await createSpec({
                unique_id: data.name,
                vertical: data.vertical,
                description: data.description,
                product: data.product,
                type: data.type,
                visible: data.visible,
            });

            setSpecs(prev => [...prev, newSpec]);
            toast.success("Spec created");
            setCreatingSpec(false);
        } catch (err) {
            toast.error("Failed to create spec");
        }
    };
    const handleDelete = (spec: Spec) => {
        setDeletingSpec(spec);
    };

    const confirmDelete = async (spec: Spec) => {
        try {
            await deleteSpec(spec.unique_id);
            await deleteSpecFile(spec.unique_id);
            setSpecs(prev => prev.filter(s => s.unique_id !== spec.unique_id));
            toast.success("Spec deleted");
        } catch (err) {
            toast.error("Failed to delete spec");
        } finally {
            setDeletingSpec(null);
        }
    };

    return (
        <>
            <div className="p-6 min-h-screen bg-gray-100 dark:bg-gray-900">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Manage Specs
                    </h1>

                    {/* show only for super admin */}
                    <button
                        onClick={() => setCreatingSpec(true)}
                        className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
                    >
                        Add Spec
                    </button>
                </div>

                {specs.map((spec) => (
                    <ManageSpecsCard
                        key={spec.unique_id}
                        name={spec.unique_id}
                        product={toProduct(spec.product[0])}
                        type={toType(spec.type)}
                        onDelete={() => handleDelete(spec)}
                    />
                ))}
            </div>

            {creatingSpec && (
                <CreateSpecModal
                    onClose={() => setCreatingSpec(false)}
                    onConfirm={(data) =>
                        handleCreate({
                            ...data,
                            product: [data.product],
                        })
                    }
                />
            )}

            {deletingSpec && (
                <DeleteSpecModal
                    spec_id={deletingSpec.unique_id}
                    onClose={() => setDeletingSpec(null)}
                    onConfirm={() => confirmDelete(deletingSpec)}
                />
            )}
        </>
    );
}